import { GENERIC_PACKAGING_TYPE_ID, MAIN_BODY_DIFFERS_FROM_PACKAGING_TYPE, PackagingUnitTypeService } from '../../../navigation/services/packaging-unit-type-service';
import { ComponentHandler } from 'src/app/services/component-services/component-handler';
import { PackagingComponentTypesEnum } from 'src/app/model/packaging-component-types-enum';
import { DirectoryDto } from './../../../data-transfer/entities/directory-dto';
import { TranslocoService } from '@jsverse/transloco';
import { DirectoryApiService } from '../../../data-transfer/services/directory-api-service';
import { SimpleAlertDialogComponent, SimpleDialogData } from './../../dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { SelectLocationDialogComponent } from './../../directory-tree/dialogs/select-location-dialog/select-location-dialog.component';
import { ComponentApiService } from '../../../data-transfer/services/component-api-service';
import { ComponentTypeService } from './../../../navigation/services/component-type-service';
import { ADD_WITH_TRACKING, SelectionDialogComponentsComponent } from '../../dialogs/selection-dialog-components/selection-dialog-components.component';
import { Subscription, firstValueFrom, forkJoin, mergeMap } from 'rxjs';
import { Component, Input, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { ComponentTableInfo, PackagingComponentPrototype } from 'src/app/model/components/packaging-component-prototype';
import { DeleteDialogData, DeleteItemDialogComponent } from '../../dialogs/delete-item-dialog/delete-item-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { NgxSpinnerService } from 'ngx-spinner';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { DialogActions } from 'src/app/model/dictionary';
import { PackagingComponentEntryDto } from 'src/app/data-transfer/entities/component-entities/packaging-component-entry-dto';
import { PackagingUnitDto } from 'src/app/data-transfer/entities/packaging-unit-entities/packaging-unit-dto';
import { CustomFieldAssignmentsDto } from 'src/app/data-transfer/entities/custom-field-assignments-dto';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CompositeMaterialSevice } from 'src/app/services/material-services/composite-material-service';
import { RelevantAnalysisTypes } from 'src/app/util/analyses-util/relevant-analysis-service';
import { HeaderInfoDialogComponent } from '../../dialogs/header-info-dialog/header-info-dialog.component';

@Component({
  selector: 'app-additional-packaging-component',
  templateUrl: './additional-packaging.component.html',
  styleUrls: ['./additional-packaging.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed,void', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})

export class AdditionalPackagingComponent implements OnChanges, OnDestroy {

  @Input() public componentEntries!: PackagingComponentEntryDto[];
  @Input() public packagingUnit!: PackagingUnitDto;
  @Input() public handler!: ComponentHandler;
  @Input() public packagingTypeId?: number;
  @Input() public componentType!: number;
  @Input() public isTracked = true;
  @Input() public isUserValidator = false;
  @Input() public isPreview = false;
  @Input() public isDarkTheme = false;
  @Input() isSingleComponent = false;
  @Input() canEditForm = true;
  @Input() displayComponentTypeImage = true;
  @Input() isDataExchangeSubRequest = false;
  @Input()
  get relevantAnalysisTypes(): RelevantAnalysisTypes {
    return this._relevantAnalysisTypes;
  }
  set relevantAnalysisTypes(newValue: RelevantAnalysisTypes) {
    this._relevantAnalysisTypes = newValue;
    this.relevantAnalysisTypesChange.emit(newValue);
  }
  @Output() relevantAnalysisTypesChange = new EventEmitter();
  @Output() packagingTypeChanged = new EventEmitter();
  @Output() volumeChanged = new EventEmitter(true);
  @Output() componentCountChanged = new EventEmitter();

  private _relevantAnalysisTypes: RelevantAnalysisTypes = new RelevantAnalysisTypes(false,false,false);
  componentsForm!: FormGroup;
  dialogActions = DialogActions;
  tableInfo!: ComponentTableInfo;

  dataSource: MatTableDataSource<FormGroup>;
  displayedColumns: { id: string, text: string }[] = [];
  displayedColumnNames: string[] = [];
  componentTypeImageSource = '';
  componentPrototype!: PackagingComponentPrototype;

  expandedElement: FormGroup<any> | null = null;

  private displayTrackedColumn = false;
  private translation = (text: string, params?: any) => this.translateService.translate(text, params);
  private hasChanged = false;

  private dialogSubscription?: Subscription;
  private componentByTypeSubscription?: Subscription;
  private saveComponentSubscription?: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private componentTypeService: ComponentTypeService,
    protected packagingUnitTypeService: PackagingUnitTypeService,
    private componentApiService: ComponentApiService,
    private directoryApiService: DirectoryApiService,
    private translateService: TranslocoService,
    private spinner: NgxSpinnerService
  ) {
    this.dataSource = new MatTableDataSource<FormGroup>();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.packagingTypeId) {
      if (changes.packagingTypeId.currentValue == null) {
        this.packagingTypeId = GENERIC_PACKAGING_TYPE_ID;
      }
      if (changes.packagingTypeId.currentValue !== changes.packagingTypeId.previousValue &&
        this.componentType === PackagingComponentTypesEnum.MainBody) {
        this.setComponentImage();
      }
      if (changes.componentEntries) {
        this.componentPrototype = this.handler.components[this.componentType];
        this.tableInfo = this.componentPrototype.getComponentTableInfo(this.translation);
        this.componentsForm = this.componentPrototype.getFormFromComponentEntries(
          this.componentEntries, this.handler, this.formBuilder, this.isTracked);
        this.componentsForm.valueChanges.subscribe(_ => {
          this.communicateComponentChanges();
        })
        this.setTableDataSource();
        this.setTableColumns();
        this.setComponentImage();
      }
    }
  }

  get componentsFormArray(): FormArray {
    return this.componentsForm?.controls.packagingComponents as FormArray;
  }

  get anyElements(): boolean {
    return this.componentsForm.getRawValue().packagingComponents.length > 0;
  }

  onExpandClicked(element: any, event: MouseEvent) {
    event.stopPropagation();
    this.expandedElement = this.expandedElement == element ? null : element;
    const target = event.target as HTMLElement;
    const tableRow = target.closest('mat-row') as HTMLElement;
    if (this.expandedElement == element) {
      setTimeout(() => {
        tableRow?.scrollIntoView({ behavior: 'smooth' });
      }, 225);
    }
  }

  openHeaderInfo(element: any, event: MouseEvent) {
    event.stopPropagation();
    let index = -1;
    if (element) {
      index = this.dataSource.data.indexOf(element);
    }

    const componentFormValue = this.componentsForm.getRawValue().packagingComponents[index];
    const component = this.componentPrototype.getSingleComponentEntryFromForm(componentFormValue, false);

    const dialogData = {
      headerInfoData: component.underlyingComponent
    };
    this.dialog.open(HeaderInfoDialogComponent, getDialogConfig(dialogData, '1200px'));
  }

  private setTableDataSource() {
    this.dataSource.data = this.componentsFormArray.controls as FormGroup[];
  }

  private setTableColumns() {
    this.setDisplayTrackedColumn();
    const actionColumn = { id: 'action', text: this.translation('dataManagement.actions') };
    this.displayedColumns = this.tableInfo.columns.concat(actionColumn);
    if (this.displayTrackedColumn) {
      const trackingColumn = { id: 'tracked', text: this.translation('dataManagement.tracking') };
      this.displayedColumns.unshift(trackingColumn);
    }
    this.displayedColumnNames = this.displayedColumns.map(x => x.text);
    this.displayedColumnNames.unshift('expand');
  }

  private setComponentImage() {
    if (this.componentType === PackagingComponentTypesEnum.MainBody) {
      this.componentTypeImageSource = MAIN_BODY_DIFFERS_FROM_PACKAGING_TYPE.includes(this.packagingTypeId ?? -1) ?
        this.packagingUnitTypeService.getMainBodyTypeImage(this.packagingTypeId) :
        this.packagingUnitTypeService.getPackagingUnitTypeImage(this.packagingTypeId);
    } else {
      this.componentTypeImageSource = this.componentTypeService.getComponentTypeImage(this.componentType, this.isDarkTheme);
    }
  }

  openDeleteDialog(element: FormGroup | null, event: MouseEvent) {
    event.stopPropagation();
    let index = -1;
    if (element) {
      index = this.dataSource.data.indexOf(element);
    }
    const dialogData: DeleteDialogData = {
      dialogHeader: this.translation('component.deleteComponentsHeader'),
      dialogText: this.translation('component.deleteComponentsText')
    };
    const dialogConfig = getDialogConfig(dialogData, '500px');
    const dialogRef = this.dialog.open(DeleteItemDialogComponent, dialogConfig);
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event !== DialogActions.DELETE) { return; }
      this.componentsFormArray.removeAt(index);
      this.hasChanged = true;
      this.setTableDataSource();
      this.setTableColumns();
    });
  }

  addNewElement(event: MouseEvent) {
    event.stopPropagation();
    const newComponent: FormGroup = this.componentPrototype.getFormFromSingleComponentEntry(
        this.componentPrototype.getEmptyComponentEntry(), this.handler, this.formBuilder, this.isTracked);
    const compositeMaterial = CompositeMaterialSevice.createEmptyCompositeMaterial();
    newComponent.controls.multiMaterial.patchValue(compositeMaterial);

    this.componentsFormArray.push(newComponent);
    this.hasChanged = true;

    this.componentsForm.updateValueAndValidity();
    this.componentPrototype.setComponentSpecificTableData(newComponent);
    this.setTableDataSource();
    this.expandedElement = newComponent;

    const target = event.target as HTMLElement;
    const tableRows = target.closest('.table-container-wide')?.querySelectorAll('mat-row') as NodeListOf<HTMLElement>;
    if (tableRows == null || tableRows.length < 2) { return; }
    setTimeout(() => {
      tableRows?.[tableRows.length - 2]?.scrollIntoView({ behavior: 'smooth' });
    }, 225);
  }

  communicateComponentChanges() {
    if (this.componentType === PackagingComponentTypesEnum.MainBody) {
      const componentForm = this.componentsFormArray.controls[0] as FormGroup;
      if (!componentForm) { return; }
      if (componentForm.controls.packagingComponentSubtypeId.value !== this.packagingTypeId) {
        this.packagingTypeChanged.emit(componentForm.controls.packagingComponentSubtypeId.value);
      }
      this.volumeChanged.emit(componentForm.controls.volume.value)
    } else {
      this.componentCountChanged.emit({ key: this.componentType, count: this.componentsFormArray.length });
    }
  }

  async openAddExistingDialog() {
    this.spinner.show();
    let componentsToDisplay: string[];
    if (this.tableInfo.id === PackagingComponentTypesEnum.Decoration) {
      componentsToDisplay = this.componentTypeService.getDecorationTypeNames();
    } else {
      componentsToDisplay = [this.tableInfo.label];
    }

    let displayedColumns = ['id', 'packagingComponentSubtypeName', 'articleName', 'articleNumber', 'manufacturer'];
    displayedColumns = displayedColumns.concat(this.isSingleComponent ? 'single-select' : 'select');

    const dialogTitle = this.tableInfo.text;
    const componentCategoryId = this.tableInfo.id;

    const dialogConfig = getDialogConfig({ displayedColumns, dialogTitle, componentCategoryId, isDataExchangeSubRequest: this.isDataExchangeSubRequest }, '1100px');
    this.spinner.hide();
    const dialogRef = this.dialog.open(SelectionDialogComponentsComponent, dialogConfig);
    this.dialogSubscription = dialogRef.afterClosed().subscribe(async result => {
      if (result.event === DialogActions.REJECT) { return; }
      const componentIds = result.data as number[];

      const observables = componentIds.map((componentId: number) => {
        if (this.tableInfo.id === PackagingComponentTypesEnum.Decoration) {
          return this.componentApiService.getPackagingComponentById(componentId).pipe(
            mergeMap(component => {
              const componentTypeName = this.componentTypeService.getComponentTypeNameById(component.packagingComponentTypeId);
              return this.componentApiService.getPackagingComponent(componentTypeName, componentId);
            }
          ));
        } else {
          return this.componentApiService.getPackagingComponent(componentsToDisplay[0], componentId);
        }
      });

      if (observables.length === 0) {
        return;
      }

      const packagingComponents = await firstValueFrom(forkJoin(observables));

      const componentEntries: PackagingComponentEntryDto[] =
        packagingComponents.map(x => this.componentPrototype.wrapComponentInComponentEntry(x));

      const isWithTracking = result.event === ADD_WITH_TRACKING;

      for (const componentEntry of componentEntries) {
        componentEntry.customFields = componentEntry.underlyingComponent.customFields;

        if (isWithTracking) {
          componentEntry.underlyingComponent.customFields = new CustomFieldAssignmentsDto();
        }
      }

      if (isWithTracking) {
        for (const componentEntry of componentEntries) {
          componentEntry.hasInternalTracking = true;
        }
      }

      const addedComponentsForm: FormGroup = this.componentPrototype.getFormFromComponentEntries(
        componentEntries, this.handler, this.formBuilder, isWithTracking || this.isTracked);
      this.doImport((addedComponentsForm.controls.packagingComponents as FormArray).controls as FormGroup[]);
      this.setTableColumns();
    });
  }

  private setDisplayTrackedColumn() {
    this.displayTrackedColumn = this.componentsFormArray.value.filter((x: any) => x.hasInternalTracking).length > 0;
  }

  private doImport(componentColtrols: FormGroup[]) {
    for (const addedComponentControl of componentColtrols) {
      this.componentsFormArray.controls.push(addedComponentControl);
      this.hasChanged = true;
    }
    this.componentsFormArray.updateValueAndValidity();
    this.componentsForm.updateValueAndValidity();
    this.setTableDataSource();
    if (this.tableInfo.id === PackagingComponentTypesEnum.Closure ||
      this.tableInfo.id === PackagingComponentTypesEnum.Decoration ||
      this.tableInfo.id === PackagingComponentTypesEnum.Inlay ||
      this.tableInfo.id === PackagingComponentTypesEnum.PackagingAid) {
      this.showAutofillDataDialog();
    }
  }

  async saveComponent(element: FormGroup | null, event: MouseEvent) {
    event.stopPropagation();
    let index = -1;
    if (element) {
      index = this.dataSource.data.indexOf(element);
    }
    const rootDirDto = await firstValueFrom(this.directoryApiService.getDirectories());
    const targetDirectoryId = await this.showSelectDirectoryDialog(rootDirDto);
    if (targetDirectoryId == null) { return; }

    const componentFormValue = this.componentsForm.getRawValue().packagingComponents[index];
    const componentToSave = this.componentPrototype.getSingleComponentEntryFromForm(componentFormValue, false);
    componentToSave.underlyingComponent.id = undefined; // saving as new component, so ID must be unset
    componentToSave.underlyingComponent.directoryId = targetDirectoryId;
    componentToSave.underlyingComponent.distributionCountries = [];

    let componentTypeName;
    if (this.tableInfo.id === PackagingComponentTypesEnum.Decoration) {
      const decorationSubtypeId = componentToSave.underlyingComponent.packagingComponentSubtypeId;
      componentTypeName = this.componentTypeService.getDecorationSubtypeNameBySubtypeId(decorationSubtypeId);
    } else {
      componentTypeName = this.tableInfo.label;
    }
    this.saveComponentSubscription = this.componentApiService
      .putPackagingComponent(componentToSave.underlyingComponent, componentTypeName)
      .subscribe({
        next: _ => {
          this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(this.handler.getComponentSucessDialogData(), '350px'));
        },
        error: e => {
          console.log('Error has occured when saving component. ', e);
        },
      });
  }

  getComponentEntries() {
    this.componentsFormArray.updateValueAndValidity();
    return this.componentPrototype.getComponentEntriesFromForm(this.componentsFormArray.getRawValue());
  }

  private showSelectDirectoryDialog(rootFolder: DirectoryDto): Promise<number> {
    const dialogConfig = getDialogConfig({ rootFolder, unreachableFolders: [] }, '500px');
    const dialogRef = this.dialog.open(SelectLocationDialogComponent, dialogConfig);

    return dialogRef.afterClosed().toPromise().then(result => {
      if (result.event !== DialogActions.REJECT) {
        return Promise.resolve(result.data.id);
      }
    });
  }

  private showAutofillDataDialog(): void {
    const data: SimpleDialogData = {
      title: this.translation('common.text.information'),
      messages: [this.translation('warnings.incompleteComponentImported')], icon: 'info'
    };
    const dialogConfig = getDialogConfig(data, '500px');
    this.dialog.open(SimpleAlertDialogComponent, dialogConfig);
  }

  public isPristine() {
    if (this.hasChanged) {
      return false;
    }
    return this.componentsFormArray.controls.find(x => !x.pristine) == undefined;
  }

  ngOnDestroy(): void {
    this.dialogSubscription?.unsubscribe();
    this.componentByTypeSubscription?.unsubscribe();
    this.saveComponentSubscription?.unsubscribe();
  }
}
